import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from "react";
import PageLoading from "../../components/PageLoading";
import {
  PageLoadingState,
  PageLoadingContextType,
} from "./PageLoadingContext.types";

// 默认状态
const defaultState: PageLoadingState = {
  isVisible: false,
  isInit: false,
  progress: 0,
  showProgress: true,
  hideLogo: false,
};

// 创建上下文
const PageLoadingContext = createContext<PageLoadingContextType | undefined>(
  undefined
);

// Provider 组件
export const PageLoadingProvider: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  const [state, setState] = useState<PageLoadingState>(defaultState);

  // 显示加载页面
  const show = useCallback((options: Partial<PageLoadingState> = {}) => {
    setState(prev => ({
      ...prev,
      isVisible: true,
      ...options,
    }));
  }, []);

  // 隐藏加载页面
  const hide = useCallback(() => {
    setState(prev => ({
      ...prev,
      isVisible: false,
    }));
  }, []);

  // 更新进度
  const updateProgress = useCallback((progress: number) => {
    setState(prev => ({
      ...prev,
      progress: Math.max(0, Math.min(100, progress)),
    }));
  }, []);

  // 设置初始化状态
  const setInit = useCallback((isInit: boolean) => {
    setState(prev => ({
      ...prev,
      isInit,
    }));
  }, []);

  // 重置状态
  const reset = useCallback(() => {
    setState(defaultState);
  }, []);

  const contextValue: PageLoadingContextType = {
    state,
    show,
    hide,
    updateProgress,
    setInit,
    reset,
  };

  return (
    <PageLoadingContext.Provider value={contextValue}>
      {children}
      {/* 全局 PageLoading 组件 */}
      {state.isVisible && (
        <PageLoading
          init={state.isInit}
          hideLogo={state.hideLogo}
          customIcon={state.customIcon}
        />
      )}
    </PageLoadingContext.Provider>
  );
};

// Hook 用于使用 PageLoading 上下文
export const usePageLoading = (): PageLoadingContextType => {
  const context = useContext(PageLoadingContext);
  if (context === undefined) {
    throw new Error("usePageLoading must be used within a PageLoadingProvider");
  }
  return context;
};
